/**
 * C-323c · Aviso de daños abiertos de una unidad.
 *
 * Lo usan las pantallas donde se registra la venta o la entrega (Checklist de Entrega FR-11, Remisión, Entrega)
 * para decir ANTES de capturar: «esta unidad tiene N daño(s) por corregir antes de entregarla».
 * El candado real (no liberar / no entregar) está en el servidor; esto sólo avisa.
 *
 *   const info = await consultarDanos(numSerie);          // null si no se pudo consultar
 *   contenedor.innerHTML = avisoDanosHtml(info, { bloquea: false });
 */

import api from '../services/api.js';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export async function consultarDanos(numSerie) {
  if (!numSerie) return null;
  try {
    const r = await api.get(`/calidad/danos-abiertos/${encodeURIComponent(String(numSerie).trim())}`);
    return r?.data || null;
  } catch (e) {
    return null; // el aviso nunca impide trabajar; el candado del servidor sí
  }
}

/**
 * @param {object|null} info  respuesta de consultarDanos
 * @param {object} op  { bloquea: true → rojo «no se puede entregar»; false → ámbar «aviso» ; accion: 'entregar'|'liberar' }
 */
export function avisoDanosHtml(info, op = {}) {
  if (!info || !info.total) return '';
  const n = info.total;
  // C-323d: con el candado en modo aviso, lo que antes bloqueaba sólo advierte (y el servidor deja constancia)
  const enModoAviso = !!op.bloquea && info.modo === 'aviso';
  const bloquea = !!op.bloquea && !enModoAviso;
  const fondo = bloquea ? '#fef2f2' : '#fffbeb';
  const borde = bloquea ? '#dc2626' : '#d97706';
  const texto = bloquea ? '#991b1b' : '#92400e';
  const titulo = bloquea
    ? `No se puede ${op.accion || 'entregar'}: la unidad tiene ${n} daño${n === 1 ? '' : 's'} por corregir`
    : enModoAviso
      ? `Esta unidad tiene ${n} daño${n === 1 ? '' : 's'} abierto${n === 1 ? '' : 's'}`
      : `Esta unidad tiene ${n} daño${n === 1 ? '' : 's'} por corregir antes de entregarla`;
  const partes = [];
  if (info.pendientes) partes.push(`${info.pendientes} pendiente${info.pendientes === 1 ? '' : 's'}`);
  if (info.en_proceso) partes.push(`${info.en_proceso} en proceso`);
  if (info.diferidos) partes.push(`${info.diferidos} se corrige${info.diferidos === 1 ? '' : 'n'} con el pedido`);
  const lista = (info.danos || []).slice(0, 6).map((d) =>
    `<li>#${d.id} · ${esc(d.descripcion)} <em style="opacity:.8">(${esc(d.estatus_texto || d.estatus)}${d.diferido_nota ? ': ' + esc(d.diferido_nota) : ''})</em></li>`
  ).join('');
  return `
    <div class="aviso-danos" data-danos="${n}" role="alert"
      style="background:${fondo};border:1px solid ${borde};border-left:5px solid ${borde};color:${texto};border-radius:8px;padding:0.8rem 1rem;margin:0.75rem 0;font-size:0.9rem;">
      <div style="font-weight:700;">⚠️ ${esc(titulo)}</div>
      ${partes.length ? `<div style="margin-top:0.2rem;">${esc(partes.join(' · '))}</div>` : ''}
      ${lista ? `<ul style="margin:0.4rem 0 0 1.1rem;padding:0;">${lista}</ul>` : ''}
      <div style="margin-top:0.4rem;font-size:0.8rem;">
        ${bloquea
          ? 'Corrígelos en Calidad → Producto No Conforme, o pide a Calidad que autorice una concesión con su motivo.'
          : enModoAviso
            ? `Puedes ${esc(op.accion || 'entregar')} la unidad: el candado está en <strong>modo aviso</strong>. Quedará constancia de quién lo hizo, cuándo y con qué daños abiertos. Lo correcto es corregirlos en Calidad → Producto No Conforme o que Calidad autorice una concesión.`
            : info.modo === 'aviso'
              ? 'Puedes registrar el documento. Si la unidad se entrega con daños abiertos, quedará constancia (candado en modo aviso).'
              : 'Puedes registrar el documento, pero la unidad no se podrá entregar mientras tenga daños abiertos (salvo concesión de Calidad).'}
      </div>
    </div>`;
}
